import { FocusContext } from '@noriginmedia/norigin-spatial-navigation'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getLocalizationVerificationCode } from '../../actions/contentActions.js'
import FocusableButtonTS from '../../components/FocusableButton/FocusableButtonTS'
import { useHybridFocusable } from '../../hooks/useHybridFocusable'
import tts from '../../lib/TTS.js'
import { store } from '../../store.js'
import { getSupportCode } from './utils.js'

import { useNavigate } from 'react-router'
import {
  platformStoreDelete,
  platformStoreGet,
  triggerSentryError,
} from '../../../../shared/helpers.js'
import Spinner from '../../components/Spinner/SpinnerTS'
import {
  LOCALIZATION_AUTH_CACHE_KEY,
  LocalizationAuthCache,
} from '../../components/VerifyLocation/types'
import useAsync from '../../hooks/useAsync'
import { useAppSelector } from '../../hooks/useStore'
import { loadStationSchedules } from '../../lib/utils/station/stationUtils'
import { useErrorModal } from '../VideoDetailScreen/context/ErrorModalProvider'
import type { LocalizationCodeResponse, Station } from './types'

const COMPONENT_FOCUS_KEY = 'LIVE_TV_WITH_STATIONS'
const VERIFY_BUTTON_FOCUS_KEY = `${COMPONENT_FOCUS_KEY}_VERIFY_BUTTON`
const STATION_BUTTON_FOCUS_KEY = `${COMPONENT_FOCUS_KEY}_STATION_BUTTON`
const TITLE = 'Verify Your Location to Watch Live TV'
const LIVESTREAM_HELP_URL = 'https://www.pbs.org/livestreamhelp'
const LIVESTREAM_HELP_DISPLAY = 'www.pbs.org/livestreamhelp'

type LiveTVWithStationsProps = {
  stations: Array<Station>
  handleUserLocalized?: () => void
}

const LiveTVWithGPSVerification = ({
  stations,
  handleUserLocalized,
}: LiveTVWithStationsProps) => {
  const [supportCode, setSupportCode] = useState<string>('')
  const [readWholeScreen, setReadWholeScreen] = useState<boolean>(false)
  const { ref, focusKey, hasFocusedChild } = useHybridFocusable({
    focusKey: COMPONENT_FOCUS_KEY,
    trackChildren: true,
  })
  const [isLocalLoading, setIsLocalLoading] = useState<boolean>(false)

  const hasSupportCode = Boolean(supportCode)
  const { station_id: stationId, call_sign: callSign } = useAppSelector(
    (state) => state.station
  )
  const navigate = useNavigate()
  const { setErrorModalVisible, setErrorModalNotVisible, setErrorModalProps } =
    useErrorModal()

  const {
    loading,
    error,
    value,
    execute: getLocalizationData,
  } = useAsync<LocalizationCodeResponse, string>({
    callback: (stationId) => getLocalizationVerificationCode(stationId),
    dependencies: [],
    auto: false,
    initialArgs: stationId,
    preserveValueWhileLoading: false,
  })

  const isLoading = useMemo(
    () => loading || isLocalLoading,
    [loading, isLocalLoading]
  )

  const SUPPORT_CODE_TEXT = `Support Code: ${supportCode}`
  const stationCount = stations.length
  const MORE_MESSAGE_TEXT = 'Select another station in your broadcast area:'

  const getFullScreenReadout = () => {
    const sections = [
      TITLE,
      `The ${callSign} Livestream is not available because we detect that you may not be in the station broadcast coverage area.`,
      'You may verify your location using a personal device.',
      `For more information, visit: ${LIVESTREAM_HELP_DISPLAY}.`,
    ]

    if (hasSupportCode) {
      sections.push(SUPPORT_CODE_TEXT)
    }

    if (stationCount > 0) {
      sections.push(MORE_MESSAGE_TEXT)
    }

    return sections.join(' .... ')
  }

  const validateLocalizationAuthCache = () => {
    const cacheData = platformStoreGet(LOCALIZATION_AUTH_CACHE_KEY)

    if (!cacheData || !stationId) return null

    const { station_id: cachedStationId, expiresAt }: LocalizationAuthCache =
      JSON.parse(cacheData)

    if (cachedStationId !== stationId) {
      platformStoreDelete(LOCALIZATION_AUTH_CACHE_KEY)
      return null
    }

    const currentTime = Math.floor(Date.now() / 1000)
    if (currentTime >= expiresAt) {
      platformStoreDelete(LOCALIZATION_AUTH_CACHE_KEY)
      return null
    }

    return cachedStationId
  }

  const validateLiveStreamStationCached = async () => {
    const cachedStationId = validateLocalizationAuthCache()

    if (!cachedStationId) return false

    await loadStationSchedules(cachedStationId)

    return true
  }

  const initialValidation = async () => {
    setIsLocalLoading(true)

    try {
      if (await validateLiveStreamStationCached()) {
        handleUserLocalized?.()
        return
      }

      store.dispatch({
        type: 'SET_SCHEDULES',
        payload: {
          channels: [],
        },
      })

      const newSupportCode = await getSupportCode()
      setSupportCode(newSupportCode)
    } finally {
      setIsLocalLoading(false)
    }
  }

  useEffect(() => {
    initialValidation()
  }, [])

  useEffect(() => {
    if (!hasFocusedChild) {
      setReadWholeScreen(true)
    }
  }, [hasFocusedChild])

  const handleButtonFocus = (index: number, title: string) => {
    tts.stopRead()
    let buttonTts = `Button ${index + 1} of ${stationCount} .... ${title}.`
    if (readWholeScreen) {
      buttonTts = `${getFullScreenReadout()} .... ${buttonTts}`
    }
    tts.addTextToRead(buttonTts)
    setReadWholeScreen(false)
  }

  const handleVerifyFocus = () => {
    tts.stopRead()
    const verifyTts = readWholeScreen
      ? `${getFullScreenReadout()} .... Verify your location button.`
      : 'Verify your location button.'
    tts.addTextToRead(verifyTts)
    setReadWholeScreen(false)
  }

  const handleEnterPress = (station: Station) => () => {
    tts.stopRead()
    loadStationSchedules(station.cid, station, true)
    tts.pushTextToRead(`Selected ${station.shortName} station.`)
  }

  const handleVerifyEnter = () => {
    getLocalizationData(stationId)
  }

  const toggleErrorModal = useCallback(
    (title: string) => {
      setErrorModalVisible()
      setErrorModalProps({
        title,
        buttons: [
          {
            label: 'Go Back',
            onEnterPress: () => setErrorModalNotVisible(),
            focusKey: 'goBackButton',
          },
        ],
        hideStationLogo: false,
        description: 'Go back and try again',
        error: null,
      })
    },
    [setErrorModalVisible, setErrorModalProps, setErrorModalNotVisible]
  )

  useEffect(() => {
    if (value) {
      navigate('/locate-screen', { state: value })
    }
    if (error) {
      toggleErrorModal('There was an error verifying your location')
      triggerSentryError(
        'handleVerifyEnter',
        'Verifying location',
        {
          errorMessage: error.message ?? 'Undefined error message (default)',
          errorDescription: '',
          errorLocation: 'LiveTVWithStations',
        },
        true
      )
    }
  }, [loading, value, error, navigate, toggleErrorModal])

  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={ref} className="livestream-screen__with-gps-localization">
        <div className="livestream-screen__with-gps-localization__wrapper">
          {isLoading && <Spinner />}
          {!isLoading && (
            <>
              <div className="livestream-screen__with-gps-localization__content">
                <div className="livestream-screen__with-gps-localization__title">
                  <span
                    aria-hidden="true"
                    className="livestream-screen__with-gps-localization__title-icon"
                  />
                  <h1>{TITLE}</h1>
                </div>
                <div className="livestream-screen__with-gps-localization__container">
                  <p className="livestream-screen__with-gps-localization__message">
                    {`The ${callSign} Livestream is not available because we detect that you may not be in the station broadcast coverage area.`}
                  </p>
                  <p className="livestream-screen__with-gps-localization__verify">
                    You may verify your location using a personal device:
                  </p>
                  <FocusableButtonTS
                    className="livestream-screen__with-gps-localization__verify-button"
                    focusKey={VERIFY_BUTTON_FOCUS_KEY}
                    onFocus={handleVerifyFocus}
                    onEnterPress={handleVerifyEnter}
                  >
                    Verify Your Location
                  </FocusableButtonTS>
                  {hasSupportCode && (
                    <p className="livestream-screen__with-gps-localization__support-code">
                      {SUPPORT_CODE_TEXT}
                    </p>
                  )}
                </div>
                {stationCount > 0 && (
                  <div className="livestream-screen__with-gps-localization__stations">
                    <div
                      className="livestream-screen__with-gps-localization__divider"
                      role="presentation"
                    >
                      <span className="livestream-screen__with-gps-localization__divider-line" />
                      <p className="livestream-screen__with-gps-localization__divider-text">
                        OR
                      </p>
                      <span className="livestream-screen__with-gps-localization__divider-line" />
                    </div>
                    <p className="more-message">{MORE_MESSAGE_TEXT}</p>
                    <div className="livestream-screen__with-gps-localization__station-buttons">
                      {stations.slice(0, 3).map((station, index) => (
                        <FocusableButtonTS
                          className="livestream-screen__with-gps-localization__button"
                          focusKey={index === 0 ? STATION_BUTTON_FOCUS_KEY : ''}
                          key={station.cid}
                          onEnterPress={handleEnterPress(station)}
                          onFocus={() =>
                            handleButtonFocus(index, station.shortName)
                          }
                        >
                          {station.shortName}
                        </FocusableButtonTS>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <p className="livestream-screen__with-gps-localization__footer">
                For more information, visit:{' '}
                <a href={LIVESTREAM_HELP_URL} rel="noreferrer" target="_blank">
                  {LIVESTREAM_HELP_DISPLAY}
                </a>
              </p>
            </>
          )}
        </div>
      </div>
    </FocusContext.Provider>
  )
}

export default LiveTVWithGPSVerification
